"use client";

import React, { useState } from "react";
import { useAutoAnimate } from "@formkit/auto-animate/react";
import { ArrowUpRight, Download, FileText, BookOpen, Layers, ChartBar as BarChart3, Funnel as Filter } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
const categories = [{
  id: "all",
  label: "All Publications"
}, {
  id: "research",
  label: "Research Reports"
}, {
  id: "ebooks",
  label: "Ebooks & Guides"
}, {
  id: "whitepapers",
  label: "Whitepapers"
}, {
  id: "case-studies",
  label: "Case Studies"
}];
const publications = [{
  id: "pub-1",
  category: "research",
  format: "Research Report",
  pages: "48 pages",
  year: "2025",
  title: "Global enterprise AI adoption and operational benchmarks",
  summary: "Empirical survey data assessing infrastructure spending, governance frameworks, and real-world deployment metrics across 420 enterprises.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_ai_adoption_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_ai_adoption_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_ai_adoption_cover.png-wvc-sizes",
  linkTo: "/ResearchReportsPage",
  readAction: "Read report"
}, {
  id: "pub-2",
  category: "ebooks",
  format: "Strategic Guide",
  pages: "36 pages",
  year: "2025",
  title: "The modern B2B media playbook: trust over volume",
  summary: "A practical framework for building durable readership, editorial moats, and audience retention in analytical publishing.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_publishing_playbook_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_publishing_playbook_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_publishing_playbook_cover.png-wvc-sizes",
  linkTo: "/EbooksGuidesPage",
  readAction: "Download guide"
}, {
  id: "pub-3",
  category: "whitepapers",
  format: "Whitepaper",
  pages: "24 pages",
  year: "2024",
  title: "Data governance and cross-border regulatory compliance",
  summary: "Technical architectures and privacy models designed for multi-region financial and technological institutions.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/whitepaper_governance_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/whitepaper_governance_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/whitepaper_governance_cover.png-wvc-sizes",
  linkTo: "/IndustryInsightsPage",
  readAction: "Read whitepaper"
}, {
  id: "pub-4",
  category: "case-studies",
  format: "Case Study",
  pages: "16 pages",
  year: "2024",
  title: "Institutional revops overhaul: a retrospective analysis",
  summary: "How a mid-market data provider consolidated five legacy tracking stacks without breaking revenue attribution.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/casestudy_revops_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/casestudy_revops_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/casestudy_revops_cover.png-wvc-sizes",
  linkTo: "/CaseStudiesPage",
  readAction: "View case study"
}, {
  id: "pub-5",
  category: "research",
  format: "Research Report",
  pages: "64 pages",
  year: "2024",
  title: "Quarterly tech procurement index & budget forecasts",
  summary: "Detailed quarter-by-quarter breakdowns of software renewal cycles, vendor consolidation trends, and CIO priorities.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_procurement_index_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_procurement_index_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/report_procurement_index_cover.png-wvc-sizes",
  linkTo: "/ResearchReportsPage",
  readAction: "Read report"
}, {
  id: "pub-6",
  category: "ebooks",
  format: "Executive Guide",
  pages: "30 pages",
  year: "2024",
  title: "The executive briefing on sustainable cloud unit economics",
  summary: "Essential methodologies for modeling compute efficiency, serverless waste, and margin protection at scale.",
  image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_cloud_economics_cover.png",
  imageSrcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_cloud_economics_cover.png-wvc-srcset",
  imageSizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1789806/img/guide_cloud_economics_cover.png-wvc-sizes",
  linkTo: "/EbooksGuidesPage",
  readAction: "Download guide"
}];
export default function Catalog() {
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [gridRef] = useAutoAnimate<HTMLDivElement>();
  const filteredPublications = selectedCategory === "all" ? publications : publications.filter(pub => pub.category === selectedCategory);
  return <section id="resource-grid" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="max-w-[1280px] mx-auto px-5 sm:px-8 lg:px-12">
        {/* Masthead Header */}
        <div className="mb-10 sm:mb-12">
          <div className="flex items-center gap-2 mb-3">
            <span className="h-2 w-2 rounded-full bg-primary" />
            <span className="text-xs uppercase tracking-wider font-semibold text-muted-foreground">
              Publication Library
            </span>
          </div>
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 pb-6 border-b border-border">
            <div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-semibold tracking-tight leading-tight text-foreground">
                Explore independent research and strategic guides
              </h2>
              <p className="mt-3 text-base sm:text-lg text-muted-foreground max-w-2xl font-default">
                Browse peer-reviewed analysis, industry benchmarks, and executive briefings across enterprise technology and operations.
              </p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <span className="text-xs font-mono text-muted-foreground">
                Showing {filteredPublications.length} of {publications.length} documents
              </span>
            </div>
          </div>
        </div>

        {/* Filter Toolbar */}
        <div className="mb-10 flex flex-wrap items-center justify-between gap-4 bg-muted/60 p-2 sm:p-2.5 rounded-lg border border-border">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="px-3 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground flex items-center gap-1.5 mr-1 hidden sm:flex">
              <Filter className="w-3.5 h-3.5" />
              Category
            </span>
            {categories.map((cat, i) => {
            const isActive = selectedCategory === cat.id;
            return <Button key={cat.id} data-index={i} type="button" variant={isActive ? "default" : "ghost"} size="sm" onClick={() => setSelectedCategory(cat.id)} className={`px-3.5 py-1.5 text-xs sm:text-sm font-medium rounded-md transition-colors ${isActive ? "bg-primary text-primary-foreground shadow-xs" : "text-muted-foreground hover:text-foreground hover:bg-background/80"}`}>
                  {cat.label}
                </Button>;
          })}
          </div>

          <div className="hidden lg:flex items-center gap-2 text-xs text-muted-foreground font-mono">
            <Layers className="w-3.5 h-3.5 text-primary" />
            <span>Unrestricted open access</span>
          </div>
        </div>

        {/* Dynamic Publications Grid */}
        <div ref={gridRef} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
          {filteredPublications.map((item, i) => <article key={item.id} data-index={i} className="group bg-card text-card-foreground rounded-lg border border-border flex flex-col justify-between overflow-hidden shadow-xs hover:shadow-md transition-all duration-200">
              <div>
                {/* Cover representation with publication spine effect */}
                <div className="relative aspect-16/10 bg-muted overflow-hidden border-b border-border">
                  <img src={item.image} data-wvc-srcset={item.imageSrcset} data-wvc-sizes={item.imageSizes} alt={item.title} loading="lazy" decoding="async" className="w-full h-full object-cover object-center group-hover:scale-102 transition-transform duration-300" />
                  {/* Spine Crease Overlay */}
                  <div aria-hidden="true" className="absolute inset-y-0 left-0 w-8 pointer-events-none bg-gradient-to-r from-black/20 via-white/10 to-transparent" />
                  {/* Format pill badge on top right */}
                  <div className="absolute top-3 right-3">
                    <Badge variant="secondary" className="bg-card/95 text-foreground font-mono text-[11px] border border-border shadow-xs backdrop-blur-xs">
                      {item.format}
                    </Badge>
                  </div>
                  {/* Metadata pill on bottom left */}
                  <div className="absolute bottom-3 left-3">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded bg-foreground/80 text-background font-mono text-[11px] backdrop-blur-xs">
                      <span>{item.pages}</span>
                      <span>•</span>
                      <span>{item.year}</span>
                    </span>
                  </div>
                </div>

                {/* Content Details */}
                <div className="p-6">
                  <h3 className="text-xl font-serif font-semibold text-foreground leading-snug group-hover:text-primary transition-colors">
                    <Link to={item.linkTo} className="hover:underline">
                      {item.title}
                    </Link>
                  </h3>
                  <p className="mt-3 text-sm text-muted-foreground line-clamp-3 leading-relaxed font-default">
                    {item.summary}
                  </p>
                </div>
              </div>

              {/* Card Footer with Action */}
              <div className="px-6 pb-6 pt-2 border-t border-border/50 flex items-center justify-between gap-4 mt-auto">
                <div className="flex items-center gap-2 text-xs font-mono text-muted-foreground">
                  <FileText className="w-3.5 h-3.5 text-muted-foreground" />
                  <span>PDF Document</span>
                </div>
                <Button asChild size="sm" variant="outline" className="rounded-sm font-medium">
                  <Link to={item.linkTo} className="flex items-center gap-1.5">
                    <span>{item.readAction}</span>
                    <ArrowUpRight className="w-3.5 h-3.5" />
                  </Link>
                </Button>
              </div>
            </article>)}
        </div>

        {/* Editorial Footnote Strip */}
        <div className="mt-12 sm:mt-16 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-muted-foreground">
          <div className="flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-primary" />
            <span>Methodologies are reviewed quarterly by independent editorial contributors.</span>
          </div>
          <Button asChild variant="link" className="p-0 h-auto text-xs text-foreground font-medium">
            <Link to="/AboutUsPage" className="flex items-center gap-1">
              <span>Read our publishing standards</span>
              <ArrowUpRight className="w-3 h-3" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}